import { Button, Icon } from '@patternfly/react-core'
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@patternfly/react-icons'
import { Tr, Td } from '@patternfly/react-table'
import { useCallback } from 'react'
import * as React from 'react'
import { NavLink } from 'react-router-dom'

import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import InteractionStatus from '@app/domain/interaction/components/interaction-status.component'
import {
  InteractionStatusId,
  TransmissionStatusId,
} from '@app/domain/interaction/interaction.interface'
import { RemoteWorkflowStatus } from '@app/domain/program/components/workflow-status.component'
import {
  getInteractionExpectedCompletionDateLabel,
  getInteractionLabel,
} from '@app/domain/program/program.helpers'
import type {
  IPatientProgramInteraction} from '@app/domain/program/program.interface';
import {
  IInteractionStatusId,
  ProgramEnrolmentStatusId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import { formatIsoDateTimeOffset } from '@app/i18n/formats'

interface Props {
  programEnrolmentStatus: ProgramEnrolmentStatusId
  programInteraction: IPatientProgramInteraction
  onSendInteractionToIntakeAnywhere: (programInteractionId: string) => void
  onRevokeInteraction: (programInteractionId: string) => void
  consentsToEmail: boolean
  consentsToSms: boolean
}

export const InteractionListRow = ({
  programEnrolmentStatus,
  onSendInteractionToIntakeAnywhere,
  onRevokeInteraction,
  programInteraction,
  consentsToEmail,
  consentsToSms,
}: Props) => {
  const featureFlags = useFeatureFlags()
  const renderStatus = () => {
    if (programInteraction.status === InteractionStatusId.DelegatedToRemote) {
      if (
        programInteraction.transmissionStatus === TransmissionStatusId.Failure
      ) {
        return (
          <Td>
            <Icon status="danger">
              <ExclamationCircleIcon />
            </Icon>{' '}
            <span>Failed Delivery</span>
          </Td>
        )
      } else if (
        programInteraction.transmissionStatus ===
        TransmissionStatusId.InvalidState
      ) {
        return (
          <Td>
            <Icon status="warning">
              <ExclamationTriangleIcon />
            </Icon>{' '}
            Unable to Update - Destination is Read-Only (
            {
              <RemoteWorkflowStatus
                status={programInteraction.remoteWorkflowStatus}
              />
            }
            )
          </Td>
        )
      } else {
        return (
          <Td style={{ color: 'green' }}>
            Delivered
            {programInteraction.deliveredAt
              ? `(${formatIsoDateTimeOffset(programInteraction.deliveredAt)})`
              : ''}
          </Td>
        )
      }
    } else if (
      programInteraction.status === InteractionStatusId.Removed ||
      programInteraction.interactionStatus === IInteractionStatusId.Removed
    ) {
      return (
        <Td>
          {`Removed ${
            programInteraction.removedAt
              ? `(${formatIsoDateTimeOffset(programInteraction.removedAt)})`
              : ''
          }`}
        </Td>
      )
    } else {
      return (
        <Td style={{ color: 'green' }}>
          <InteractionStatus status={programInteraction.status} />
        </Td>
      )
    }
  }

  const handleSendInteractionToIntakeAnywhere = useCallback(() => {
    onSendInteractionToIntakeAnywhere(programInteraction.programInteractionId)
  }, [onSendInteractionToIntakeAnywhere, programInteraction])

  const handleRevokeInteraction = useCallback(() => {
    onRevokeInteraction(programInteraction.programInteractionId)
  }, [onRevokeInteraction, programInteraction])

  const renderAction = (programInteraction: IPatientProgramInteraction) => {
    const isCompleted =
      programInteraction.status === InteractionStatusId.Completed
    const isInactive =
      programInteraction.status === InteractionStatusId.Cancelled ||
      programInteraction.removedAt ||
      programInteraction.interactionStatus === IInteractionStatusId.Removed ||
      programEnrolmentStatus === ProgramEnrolmentStatusId.Discontinued ||
      programEnrolmentStatus === ProgramEnrolmentStatusId.Removed

    const isRemote =
      programInteraction.interactionType ===
      ProgramInteractionTypeId.RemoteInteraction
    const isInProgress =
      programInteraction.status === InteractionStatusId.InProgress
    const isCancelled =
      programInteraction.status === InteractionStatusId.Cancelled
    const isTransmitted =
      programInteraction.status === InteractionStatusId.DelegatedToRemote
    const isFailed =
      programInteraction.transmissionStatus === TransmissionStatusId.Failure
    const isExpired = programInteraction.status === InteractionStatusId.Expired

    const isConsentGiven = consentsToEmail || consentsToSms

    const isRedeliverable =
      isRemote &&
      !isInProgress &&
      (isTransmitted || (isConsentGiven && (isFailed || isExpired)))

    if (isCompleted) {
      if (programInteraction.visitId) {
        return (
          <Td>
            <NavLink to={`/clinic/visit/${programInteraction.visitId}`}>
              <Button variant="secondary">View Results</Button>
            </NavLink>
          </Td>
        )
      }
      if (programInteraction.remoteInteractionId) {
        return (
          <Td>
            <NavLink
              to={`/clinic/interaction/${programInteraction.remoteInteractionId}`}
            >
              <Button variant="secondary">View Results</Button>
            </NavLink>
          </Td>
        )
      }
    }

    if (
      isInactive ||
      !featureFlags.isFeatureFlagEnabled(FeatureFlagId.RemoteInteraction)
    ) {
      return <Td />
    }

    if (isRedeliverable) {
      return (
        <Td>
          <Button
            variant="primary"
            onClick={handleSendInteractionToIntakeAnywhere}
            style={isTransmitted ? { margin: '0 0.5rem 0.5rem 0' } : undefined}
          >
            Re-Deliver
          </Button>
          {!isFailed && isTransmitted && (
            <Button variant="secondary" onClick={handleRevokeInteraction}>
              Revoke
            </Button>
          )}
        </Td>
      )
    }

    if (isRemote && !isInProgress && !isCancelled && isConsentGiven) {
      return (
        <Td>
          <Button
            variant="primary"
            onClick={handleSendInteractionToIntakeAnywhere}
          >
            Deliver
          </Button>
        </Td>
      )
    }

    return <Td />
  }

  const renderCompletionDetails = useCallback(() => {
    if (programInteraction.status === InteractionStatusId.Completed) {
      return (
        <Td>
          {programInteraction.interactionCompletedAt
            ? formatIsoDateTimeOffset(programInteraction.interactionCompletedAt)
            : '-'}
        </Td>
      )
    } else if (
      programInteraction.status !== InteractionStatusId.InProgress &&
      programInteraction?.expectedInteractionDate
    ) {
      return (
        <Td>
          {getInteractionExpectedCompletionDateLabel(
            new Date(),
            programInteraction.expectedInteractionDate,
          )}
        </Td>
      )
    } else {
      return <Td />
    }
  }, [programInteraction])

  return (
    <Tr>
      <Td>{getInteractionLabel(programInteraction.interactionType)}</Td>

      {programInteraction?.label ? (
        <Td>{programInteraction.label}</Td>
      ) : (
        <Td> </Td>
      )}

      {renderStatus()}

      {renderCompletionDetails()}

      <RequireFeatureFlag feature={FeatureFlagId.RemoteInteraction}>
        {renderAction(programInteraction)}
      </RequireFeatureFlag>
    </Tr>
  )
}
